<template>
    <section
        v-if="product"
        class="relative rounded-20 p-3 sm:container max-sm:rounded-tr-none lg:px-5 lg:py-5 xl:px-5">
        <div
            class="absolute inset-0 size-full overflow-hidden rounded-20 max-sm:rounded-tr-none">
            <div
                class="bg-noise size-full rounded-20 max-sm:rounded-tr-none"></div>
            <div
                :class="{ 'lg:hidden': product.focus === 'mobile' }"
                class="absolute left-0 top-0 size-[20rem] -translate-x-1/2 -translate-y-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-700" />
            <div
                :class="{ 'lg:hidden': product.focus === 'mobile' }"
                class="absolute left-full top-full size-[20rem] -translate-x-1/2 -translate-y-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-700" />
        </div>

        <div
            class="relative max-lg:space-y-5 max-lg:overflow-hidden lg:flex lg:space-x-10">
            <div class="shrink-0 lg:flex">
                <div class="flex grow flex-col">
                    <ProductImagesSlider
                        v-if="product.images.length"
                        :video-link="product.videoLink"
                        :video-preview="product.videoPreview"
                        :images="product.images"
                        class="sticky top-25" />
                </div>
            </div>

            <div class="flex grow flex-col space-y-5 xl:space-y-10">
                <h1
                    v-if="product.title"
                    class="order-1 text-20/120 font-semibold xl:text-24/133">
                    <span v-html="product.title" />
                    <span
                        v-if="config.public.website !== 'cromi.ru'"
                        class="text-ash-300">
                        в аренду
                    </span>
                </h1>
                <div
                    class="products-center order-2 flex items-center justify-between border-y border-ash-45 py-5 lg:order-3">
                    <div class="flex gap-1 max-sm:flex-col sm:gap-5">
                        <div
                            v-if="product.price"
                            class="space-y-1 text-20/120 font-semibold xl:text-32/100">
                            <p
                                v-if="config.public.website !== 'cromi.ru'"
                                class="text-16/132 text-ash-300 xl:text-24/133">
                                В аренду
                            </p>
                            <p v-html="formatProductPrice(product.price)" />
                        </div>
                        <div
                            v-if="isCromi()"
                            class="relative flex items-center gap-2 text-16/75 font-semibold leading-5 text-[#6AB429] xl:text-20/120">
                            <span
                                class="mb-1 block size-2 rounded-full bg-[#6AB429]"></span>
                            В наличии
                        </div>
                        <div
                            v-else-if="product.estimated_cost"
                            class="flex flex-col justify-center text-16/148 font-semibold text-[#6AB429] xl:text-20/120">
                            <p>Оценочная стоимость:</p>
                            <p v-html="formatPrice(product.estimated_cost)" />
                        </div>
                    </div>
                    <AtomButton
                        v-if="calculator"
                        class="flex items-center gap-2"
                        @click="handleOpenCalculatorModal">
                        <AtomIcon name="calculator" />
                        Калькулятор
                    </AtomButton>
                </div>
                <div
                    v-if="product.description"
                    v-html="product.description"
                    class="tinyeditor order-3 !mt-4 text-16/148 text-accent-100 lg:order-2" />
                <div
                    v-if="product.parameters"
                    class="order-4 space-y-4">
                    <div class="relative flex justify-between">
                        <AtomTitle level="3">
                            Технические характеристики
                        </AtomTitle>
                        <AtomIcon
                            name="chevron-down"
                            :class="{ 'rotate-180': open }"
                            class="size-6 transition-transform duration-300 lg:hidden" />
                        <button
                            aria-label="показать/скрыть технические характеристики"
                            class="absolute inset-0 lg:hidden"
                            @click="open = !open"></button>
                    </div>
                    <div
                        v-html="product.parameters"
                        class="tinyeditor text-16/150 font-medium text-accent-100 max-lg:hidden" />
                    <div class="lg:hidden">
                        <CollapsibleRoot v-model:open="open">
                            <CollapsibleContent
                                class="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
                                <div
                                    v-html="product.parameters"
                                    class="tinyeditor text-16/150 font-medium text-accent-100" />
                            </CollapsibleContent>
                        </CollapsibleRoot>
                    </div>
                </div>

                <div class="order-last flex justify-between space-x-3">
                    <div class="flex w-full gap-3 max-md:flex-col">
                        <BaseLink
                            v-if="config.public.website !== 'cromi.rent'"
                            size="accent"
                            :class="{
                                'w-full': product.modalButtonStretch,
                            }"
                            class="font-semibold max-lg:h-15 max-lg:w-full"
                            @click="handleOpenModal">
                            {{ product.modalButtonText }}
                        </BaseLink>

                        <div
                            v-if="showCartButton"
                            class="relative h-16 w-full overflow-hidden md:max-w-64">
                            <Transition
                                name="view-slide-up"
                                mode="out-in">
                                <BaseLink
                                    v-if="!count"
                                    icon-right="cart-plus"
                                    size="accent"
                                    class="h-full w-full"
                                    @click="addToCart({ slug: product.slug })">
                                    В корзину
                                </BaseLink>

                                <div
                                    v-else
                                    class="relative flex w-full rounded-full bg-black/20 backdrop-blur-sm">
                                    <span
                                        class="pointer-events-none absolute inset-0 rounded-full border border-ash-200"></span>

                                    <BaseCalculateInput
                                        key="input"
                                        size="md"
                                        v-model="count"
                                        :min="0"
                                        :max="
                                            product.max_cart_quantity ??
                                            undefined
                                        " />

                                    <NuxtLink
                                        to="/cart/"
                                        @click="closeToasts"
                                        aria-label="Перейти в корзину"
                                        class="flex size-16 shrink-0 items-center justify-center rounded-full border border-ash-200 bg-black/20 outline-none transition-[opacity,background-color] duration-300 hover:bg-ash-200/40 focus:bg-ash-200/40">
                                        <AtomIcon
                                            name="cart-to-go"
                                            class="size-6" />
                                    </NuxtLink>
                                </div>
                            </Transition>
                        </div>
                    </div>

                    <ShotVideo
                        v-if="product.videoLink"
                        :link="product.videoLink"
                        :preview="product.videoPreview"
                        class="w-15 shrink-0 lg:hidden" />
                </div>
            </div>
        </div>

        <Teleport to="body">
            <CostSetModal
                :direction-slug="directionSlug"
                :calculator="calculator" />
        </Teleport>
    </section>
</template>

<script setup lang="ts">
import { CollapsibleContent, CollapsibleRoot } from 'reka-ui'
import type { PropType } from 'vue'
import type {
    ICalculatorSetting,
    TCalculatorType,
} from '~/types/calculator.types'

import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseCalculateInput from '~/components/base/base-calculate-input/BaseCalculateInput.vue'
import BaseLink from '~/components/base/base-link/BaseLink.vue'
import ShotVideo from '~/components/base/shot-video/ShotVideo.vue'
import CostSetModal from '~/components/modals/cost-set-modal/CostSetModal.vue'
import ProductImagesSlider from '~/components/particles/product-images-slider/ProductImagesSlider.vue'
import type { ProductPreviewProps } from '~/components/particles/product-preview/product-preview.types'

import { useCart } from '~/composables/useCart'
import { useCartCountModel } from '~/composables/useCartCountModel'
import { useCartProductLimits } from '~/composables/useCartProductLimits'

const props = defineProps({
    product: {
        type: [Object, null] as PropType<ProductPreviewProps | null>,
        default: null,
    },
    calculator: {
        type: [Object, null] as PropType<ICalculatorSetting | null>,
        default: null,
    },
    directionSlug: {
        type: [String, null] as PropType<TCalculatorType | null>,
        default: null,
    },
})

const config = useRuntimeConfig()
const route = useRoute()
const modalStore = useModalStore()
const { closeAll: closeToasts } = useToastStore()

const showCartButton = computed(() => {
    return (
        config.public.website === 'cromi.ru' && route.path.includes('catalog')
    )
})

const { addToCart } = useCart()
const { applyProductLimit } = useCartProductLimits()
const count = useCartCountModel(() => props.product, { min: 0 })

watch(
    () => [props.product?.slug, props.product?.max_cart_quantity],
    () => {
        applyProductLimit(props.product)
    },
    {
        immediate: true,
    }
)

const open = ref<boolean>(false)

function formatProductPrice(
    price: ProductPreviewProps['price']
): string | undefined {
    if (!price) return undefined

    return `от ${formatPrice(price)}`
}

function handleOpenModal(): void {
    const formID = route.fullPath.includes('catalog') ? 6 : 5
    modalStore.setFormID(isRadioguide() ? 13 : formID)

    modalStore.setNameProduct(
        props.product?.feedback_label ||
            (props.product?.title
                ? props.product?.title[0].toLowerCase() +
                  props.product?.title.slice(1)
                : '')
    )

    modalStore.setFormName(
        route.name === 'direction-subcategory'
            ? 'Модельный ряд'
            : 'Страница товара'
    )
    modalStore.open('feedbackModal')
}

function handleOpenCalculatorModal() {
    modalStore.setFormName(isCromi() ? 'Страница продукта, "Калькулятор"' : '')

    modalStore.open('costSet')
}
</script>
